import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { adminAPI } from '@/api/client';
import { toast } from 'sonner';
import { Shield, ShieldOff, Trash2, RefreshCw, Plus, AlertTriangle, Activity, Ban, CheckCircle } from 'lucide-react';

// API client for security endpoints
const securityAPI = {
  getBlacklist: (includeInactive = false) => 
    adminAPI.getBlacklist ? adminAPI.getBlacklist(includeInactive) : 
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/blacklist?include_inactive=${includeInactive}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
  
  getAttempts: (ip = null, limit = 50) => 
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/attempts${ip ? `?ip_address=${ip}&` : '?'}limit=${limit}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
  
  getStats: () => 
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/stats`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
  
  unblockIP: (ip) => 
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/unblock`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ip_address: ip })
    }).then(r => r.json()),
  
  blacklistIP: (ip, reason) => 
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/blacklist`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ip_address: ip, reason: reason })
    }).then(r => r.json()),
  
  clearAttempts: (ip) => 
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/attempts/${ip}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
};

const IPBlacklistManager = () => {
  const [blacklist, setBlacklist] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newIP, setNewIP] = useState('');
  const [newReason, setNewReason] = useState('');
  const [selectedIP, setSelectedIP] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [blacklistData, attemptsData, statsData] = await Promise.all([
        securityAPI.getBlacklist(showInactive),
        securityAPI.getAttempts(null, 50),
        securityAPI.getStats()
      ]);
      
      setBlacklist(blacklistData.blacklist || []);
      setAttempts(attemptsData.attempts || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch security data:', error);
      toast.error('Security data লোড করতে সমস্যা হয়েছে');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [showInactive]);

  const handleUnblock = async (ip) => {
    try {
      const result = await securityAPI.unblockIP(ip);
      if (result.success) {
        toast.success(`✅ IP ${ip} unblock করা হয়েছে`);
        fetchData();
      } else {
        toast.error(result.detail || 'Unblock করতে সমস্যা হয়েছে');
      }
    } catch (error) {
      toast.error('Unblock করতে সমস্যা হয়েছে');
    }
  };

  const handleAddToBlacklist = async () => {
    if (!newIP || !newReason) {
      toast.error('IP ও Reason দিন');
      return;
    }
    
    try {
      const result = await securityAPI.blacklistIP(newIP, newReason);
      if (result.success) {
        toast.success(`🚫 IP ${newIP} blacklist করা হয়েছে`);
        setAddDialogOpen(false);
        setNewIP('');
        setNewReason('');
        fetchData();
      } else {
        toast.error(result.detail || 'Blacklist করতে সমস্যা হয়েছে');
      }
    } catch (error) {
      toast.error('Blacklist করতে সমস্যা হয়েছে');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('bn-BD', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getAttemptTypeBadge = (type) => {
    const colors = {
      'DEV_TOOLS': 'bg-purple-500',
      'INVALID_TOKEN': 'bg-orange-500',
      'BRUTE_FORCE': 'bg-red-600',
      'RATE_LIMIT': 'bg-yellow-500',
      'SUSPICIOUS_REQUEST': 'bg-pink-500'
    };
    return <Badge className={`${colors[type] || 'bg-gray-500'} text-white text-xs`}>{type}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Ban className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{stats?.active_blacklist_count || 0}</p>
                <p className="text-sm text-red-600">Active Blocked IPs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold text-orange-700">{stats?.total_attempts_today || 0}</p>
                <p className="text-sm text-orange-600">Today's Attempts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Activity className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats?.unique_ips_today || 0}</p>
                <p className="text-sm text-blue-600">Unique IPs Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700">
                  {stats?.attempts_by_type?.DEV_TOOLS || 0}
                </p>
                <p className="text-sm text-purple-600">Dev Tools Attempts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Blacklist Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Ban className="h-5 w-5 text-red-600" />
                🚫 IP Blacklist
              </CardTitle>
              <CardDescription>Blocked IP addresses এবং তাদের reason</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchData}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700">
                    <Plus className="h-4 w-4 mr-1" />
                    Add IP
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>🚫 Manual IP Blacklist</DialogTitle>
                    <DialogDescription>
                      একটি IP address manually blacklist করুন
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>IP Address</Label>
                      <Input 
                        placeholder="192.168.1.1" 
                        value={newIP}
                        onChange={(e) => setNewIP(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Reason (কারণ)</Label>
                      <Input 
                        placeholder="Suspicious activity..." 
                        value={newReason}
                        onChange={(e) => setNewReason(e.target.value)}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-red-600" onClick={handleAddToBlacklist}>Block IP</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-3">
            <label className="flex items-center gap-2 text-sm">
              <input 
                type="checkbox" 
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded"
              />
              Show unblocked IPs too
            </label>
          </div>
          
          {blacklist.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>কোনো blocked IP নেই। সব clean! ✨</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Reason (কারণ)</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Blocked At</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blacklist.map((item, idx) => (
                  <TableRow key={idx} className={!item.is_active ? 'opacity-50' : ''}>
                    <TableCell className="font-mono font-medium">{item.ip_address}</TableCell>
                    <TableCell className="max-w-xs truncate text-sm">{item.reason}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.attempt_count}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(item.blacklisted_at)}</TableCell>
                    <TableCell>
                      {item.is_active ? (
                        <Badge className="bg-red-500 text-white">BLOCKED</Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-600">Unblocked</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.is_active && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-600 border-green-600 hover:bg-green-50"
                          onClick={() => handleUnblock(item.ip_address)}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Unblock
                        </Button>
                      )}
                      {!item.is_active && item.unblocked_by && (
                        <span className="text-xs text-gray-500">
                          by {item.unblocked_by.split('@')[0]}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent Attempts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            ⚠️ Recent Suspicious Attempts
          </CardTitle>
          <CardDescription>সাম্প্রতিক suspicious activity (last 50)</CardDescription>
        </CardHeader>
        <CardContent>
          {attempts.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <p>কোনো recent attempts নেই</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attempts.map((attempt, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-sm">{attempt.ip_address}</TableCell>
                      <TableCell>{getAttemptTypeBadge(attempt.attempt_type)}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-gray-600">
                        {attempt.details || attempt.endpoint || '-'}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(attempt.timestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IPBlacklistManager;
